import type { ColumnsType } from 'antd/es/table'
import React, { useMemo } from 'react'
import { gray } from 'sutra/tokens/colors'

import { CatalogTableNameCell } from 'features/catalog/features/table/components/CatalogTableNameCell/CatalogTableNameCell'
import { buildCatalogMetricColumns } from 'features/catalog/features/table/hooks/catalog-table-metric-columns'
import { buildCatalogSourceColumns } from 'features/catalog/features/table/hooks/catalog-table-source-columns'
import type { CatalogRowMetrics } from 'features/catalog/hooks/use-catalog-table-metrics'
import type { FocusListOption } from 'features/catalog/types'

/** Builds `CatalogTable`'s antd columns for the current lens. A plain `.ts`
 * module (uses `React.createElement` rather than JSX, so it can stay a flat
 * `hooks/` file per `colocated-hooks-flat` instead of needing its own
 * `component-directory-required` directory) — extracted so
 * `CatalogTable.tsx` stays under the component-file-too-large threshold.
 * The lens-conditional column groups (Sentences/Sentiment/Rating,
 * Countries) live in sibling plain modules to keep this file under the
 * `hook-max-lines` threshold. */
export function useCatalogTableColumns(params: {
  selectedLens: string
  groupCountries: boolean
  metricsByValue: Record<string, CatalogRowMetrics>
  currentPage: number
  pageSize: number
}): ColumnsType<FocusListOption> {
  const {
    selectedLens,
    groupCountries,
    metricsByValue,
    currentPage,
    pageSize,
  } = params

  return useMemo<ColumnsType<FocusListOption>>(() => {
    const cols: ColumnsType<FocusListOption> = [
      {
        title: '#',
        key: 'number',
        width: 48,
        // A grouped theme child (the only row shape carrying `displayLabel`)
        // renders blank — antd's `index` isn't a stable, meaningful ordinal
        // once expanded children are mixed into the numbering.
        render: (_, row, index) =>
          row.displayLabel !== undefined
            ? null
            : React.createElement(
                'span',
                {
                  style: {
                    color: gray[400],
                    fontVariantNumeric: 'tabular-nums',
                  },
                },
                String((currentPage - 1) * pageSize + index + 1),
              ),
      },
      {
        title: 'Name',
        dataIndex: 'value',
        key: 'value',
        sorter: (a, b) => a.value.localeCompare(b.value),
        render: (_, row) =>
          React.createElement(CatalogTableNameCell, { row, selectedLens }),
      },
    ]

    if (selectedLens === 'source_type') {
      cols.push(...buildCatalogSourceColumns({ groupCountries }))
    }

    cols.push({
      title: 'Records',
      dataIndex: 'n_posts',
      key: 'n_posts',
      width: 120,
      align: 'right',
      sorter: (a, b) => a.n_posts - b.n_posts,
      render: (value: number) => value.toLocaleString(),
    })

    if (selectedLens !== 'source_type') {
      cols.push(...buildCatalogMetricColumns(metricsByValue))
    }

    cols.push({
      title: 'Items',
      dataIndex: 'product_count',
      key: 'product_count',
      width: 100,
      align: 'right',
      sorter: (a, b) => (a.product_count ?? 0) - (b.product_count ?? 0),
      render: (value: number | undefined) =>
        value !== undefined ? value.toLocaleString() : '—',
    })

    return cols
  }, [selectedLens, groupCountries, metricsByValue, currentPage, pageSize])
}
